import React from "react";
import { Edit2, Search } from "lucide-react";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";

export interface CustomerPackage {
  id: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  packageName: string;
  usedMinutes: number;
  remainingMinutes: number;
  sunbedId?: number;
}

interface CustomerPackagesTableProps {
  packages: CustomerPackage[];
  indexOfFirstItem: number;
  onEdit: (pkg: CustomerPackage) => void;
  isLoading?: boolean;
}

export const CustomerPackagesTable: React.FC<CustomerPackagesTableProps> = ({
  packages,
  indexOfFirstItem,
  onEdit,
  isLoading = false,
}) => {
  return (
    <div className="flex-1 overflow-y-auto relative">
      <table className="w-full">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100">
          <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
            <th className="col-span-1 text-left font-bold">#</th>
            <th className="col-span-3 text-left font-bold">Customer</th>
            <th className="col-span-3 text-left font-bold">Package</th>
            <th className="col-span-2 text-left font-bold">Used</th>
            <th className="col-span-2 text-left font-bold">Remaining</th>
            <th className="col-span-1 text-right font-bold">Action</th>
          </tr>
        </thead>
        <tbody className="w-full divide-y divide-gray-100 bg-white">
          {isLoading ? (
            <tr className="h-64 w-full">
              <td colSpan={12} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                </div>
              </td>
            </tr>
          ) : packages.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full">
              <td className="w-full h-full flex flex-col items-center justify-center">
                <Search className="w-12 h-12 mb-3 opacity-20" />
                <p className="text-lg font-medium text-gray-500">
                  No customer packages found
                </p>
                <p className="text-sm">Try adjusting your search terms</p>
              </td>
            </tr>
          ) : (
            <>
              {packages.map((pkg, idx) => (
                <tr
                  key={pkg.id}
                  className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-purple-50/30 transition-colors bg-white group text-sm w-full"
                >
                  <td className="col-span-1 text-gray-500 font-medium">
                    {indexOfFirstItem + idx + 1}
                  </td>
                  <td className="col-span-3 min-w-0">
                    <div className="font-semibold text-gray-800 group-hover:text-[#885ABB] transition-colors">
                      {pkg.customerName || "Unknown"}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">{pkg.customerPhone || "—"}</div>
                    <div className="text-xs text-gray-500 break-all">{pkg.customerEmail || "—"}</div>
                  </td>
                  <td className="col-span-3 text-gray-600">
                    {pkg.packageName}
                  </td>
                  <td className="col-span-2 text-gray-600 font-medium">
                    {pkg.usedMinutes} mins
                  </td>
                  <td className="col-span-2">
                    <span
                      className={cn(
                        "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
                        pkg.remainingMinutes > 0
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      )}
                    >
                      {pkg.remainingMinutes} mins
                    </span>
                  </td>
                  <td className="col-span-1 flex justify-end gap-2 transition-opacity">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onEdit(pkg)}
                      className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Edit Minutes"
                    >
                      <Edit2 size={15} />
                    </Button>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
